import { ClippingMode, RenderStateCamera } from "@novorender/api";
import { quat, ReadonlyVec3, vec3 } from "gl-matrix";
import { useCallback } from "react";
import { useStore } from "react-redux";

import { Events, track } from "apis/procore";
import { uploadToFas } from "apis/procore/fas";
import type { RootState } from "app";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useLazyHidden } from "contexts/hidden";
import { useLazyHighlighted } from "contexts/highlighted";
import { GroupStatus as ObjectGroupStatus, isInternalGroup, useLazyObjectGroups } from "contexts/objectGroups";
import { useLazySelectionBasket } from "contexts/selectionBasket";
import { selectAreas } from "features/area/areaSlice";
import { createSnapshotImgAsArrayBuffer } from "features/bookmarks/utils";
import { selectCoordinates } from "features/coordinates/coordinatesSlice";
import { getCameraDir } from "features/engine2D/utils";
import { selectMeasure } from "features/measure";
import {
    getMeasurePointsFromTracer,
    LaserPoint,
    selectOutlineLasers,
    TraceMeasurement,
} from "features/outlineLaser/outlineLaserSlice";
import { selectPointLines } from "features/pointLine/pointLineSlice";
import { ObjectVisibility, selectClippingPlanes, selectDefaultVisibility, selectMainObject } from "features/render";
import { selectProcoreCompanyId, selectProcoreProjectId } from "slices/explorer";

import type {
    GetViewpointOptions,
    GetViewpointResult,
    MeasureLaser,
    MeasureLaserMeasurement,
    MeasureMeasurements,
    Snapshot,
    Viewpoint,
} from "./types";

// ---- Hook ----

export function useGetViewpoint() {
    const {
        state: { view, canvas, webviewer },
    } = useExplorerGlobals();
    const groups = useLazyObjectGroups();
    const highlighted = useLazyHighlighted();
    const hidden = useLazyHidden();
    const selectionBasket = useLazySelectionBasket();
    const store = useStore<RootState>();

    const getViewpoint = useCallback(
        async ({
            name,
            grouping,
            createSnapshot = true,
            snapshotType = "jpeg",
            snapshotWidth = 374,
            snapshotHeight = 208,
        }: GetViewpointOptions = {}): Promise<GetViewpointResult> => {
            if (!webviewer || !view || !canvas) {
                throw new Error("Not initialized yet");
            }

            // This function depends on a lot of state, but there's no point to recreate it each time that state changes,
            // so getState inside and use selectors on that state instead.
            const state = store.getState();
            const defaultVisibility = selectDefaultVisibility(state);
            const renderMode = defaultVisibility === ObjectVisibility.Xray ? "xray" : "shaded";
            const viewpointDefaultVisibility =
                defaultVisibility !== ObjectVisibility.Transparent && defaultVisibility !== ObjectVisibility.Xray;

            const viewpointCamera = buildCamera(snapshotCamera(view.renderState.camera), canvas.width, canvas.height);

            const viewpointClipping = buildClipping(state);

            const pointLines = selectPointLines(state);
            const measure = selectMeasure(state);
            const { allObjectIds, selectedIds, exceptionIds } = collectObjectIds(
                highlighted.current.idArr,
                hidden.current.idArr,
                selectionBasket.current.idArr,
                selectMainObject(state),
                viewpointDefaultVisibility,
                pointLines,
                measure,
            );

            let snapshotPromise: Promise<Snapshot> | undefined = undefined;
            if (createSnapshot) {
                const companyId = selectProcoreCompanyId(state);
                const projectId = selectProcoreProjectId(state);
                if (!companyId || !projectId) {
                    throw new Error("Company ID or project ID not found");
                }
                snapshotPromise = createAndUploadSnapshot({
                    id: crypto.randomUUID(),
                    width: snapshotWidth,
                    height: snapshotHeight,
                    type: snapshotType,
                    companyId,
                    projectId,
                });
            }

            const [snapshot, objectIdsToPublicIds] = await Promise.all([
                snapshotPromise,
                webviewer.objectIdsToPublicIdsMap(Array.from(allObjectIds)),
            ]);

            const viewpointSelection =
                selectedIds.length > 0
                    ? (selectedIds.map((id) => objectIdsToPublicIds.get(id)).filter(Boolean) as string[])
                    : undefined;
            const visibilityExceptions = exceptionIds
                .map((id) => objectIdsToPublicIds.get(id))
                .filter(Boolean) as string[];

            const viewpointMeasure = buildMeasureEntities(measure, objectIdsToPublicIds);
            const viewpointMeasurements = buildMeasurements(
                selectAreas(state),
                pointLines,
                selectCoordinates(state),
                selectOutlineLasers(state),
                viewpointMeasure,
                objectIdsToPublicIds,
            );

            const viewpointVisibility: Viewpoint["visibility"] = {
                default_visibility: viewpointDefaultVisibility,
                exceptions: {
                    object_ids: visibilityExceptions,
                },
            };

            const viewpoint: GetViewpointResult = {
                name,
                grouping,
                unit: "meters",
                render_mode: renderMode,
                snapshot,
                camera: viewpointCamera,
                clipping: viewpointClipping,
                markup: undefined,
                visibility: viewpointVisibility,
                selection: viewpointSelection,
                groups: buildGroups(groups.current),
                measurements: viewpointMeasurements,
            };

            void track(Events.viewpoint.created, getViewpointCreatedProperties(viewpoint));

            return viewpoint;
        },
        [canvas, groups, highlighted, hidden, selectionBasket, store, view, webviewer],
    );

    return getViewpoint;
}

// ---- Helper functions ----

function copyTraceMeasurement(
    measurement: TraceMeasurement | undefined,
    startAr: LaserPoint[],
    endAr: LaserPoint[],
): MeasureLaserMeasurement | undefined {
    if (!measurement) {
        return undefined;
    }

    const pts = getMeasurePointsFromTracer(measurement, startAr, endAr);
    if (!pts) {
        return undefined;
    }

    const getConnection = (point: LaserPoint | undefined) => {
        if (!point) {
            return undefined;
        }
        if (point.connection?.type === "tangent") {
            return { type: "tangent", start: point.connection.start, end: point.connection.end } as const;
        }
        if (point.connection?.type === "normal") {
            return { type: "normal", normal: point.connection.normal } as const;
        }
        return undefined;
    };

    return {
        start: {
            position: pts[0].position,
            connection: getConnection(pts[0]),
        },
        end: {
            position: pts[1].position,
            connection: getConnection(pts[1]),
        },
    } satisfies MeasureLaserMeasurement;
}

type CameraSnapshot = Pick<RenderStateCamera, "position" | "rotation" | "fov" | "kind">;

function snapshotCamera(camera: RenderStateCamera): CameraSnapshot {
    return {
        kind: camera.kind,
        fov: camera.fov,
        position: vec3.clone(camera.position),
        rotation: quat.clone(camera.rotation),
    };
}

function buildCamera(camera: CameraSnapshot, canvasWidth: number, canvasHeight: number): Viewpoint["camera"] {
    const { rotation, fov, kind } = camera;
    const position = vec3.clone(camera.position) as ReadonlyVec3;
    const direction = getCameraDir(rotation);
    const cameraUp = vec3.transformQuat(vec3.create(), vec3.fromValues(0, 1, 0), rotation);
    const aspect_ratio = canvasWidth > 0 && canvasHeight > 0 ? canvasWidth / canvasHeight : 1;

    return kind === "pinhole"
        ? {
              perspective: { position, direction, camera_up: cameraUp, fov, aspect_ratio },
          }
        : {
              orthogonal: {
                  position,
                  direction,
                  camera_up: cameraUp,
                  aspect_ratio,
                  vertical_extent: fov,
                  target_distance: 0, // TODO: Implement target distance
              },
          };
}

function buildClipping(state: RootState): Viewpoint["clipping"] {
    const clipping = selectClippingPlanes(state);

    return {
        draw_outlines: clipping.outlines,
        enabled: clipping.enabled,
        draw_planes: clipping.draw,
        mode: clipping.mode === ClippingMode.intersection ? "intersection" : "union",
        box: undefined, // TODO: Implement box clipping
        planes:
            clipping.planes.length > 0
                ? clipping.planes.map((plane) => {
                      const direction = plane.normalOffset.slice(0, 3) as ReadonlyVec3;
                      let location: vec3;
                      // If we have anchorPos - use it for location for better HUD placement.
                      // Procore engine should always have it but adding a fallback just in case.
                      if (plane.anchorPos) {
                          location = vec3.clone(plane.anchorPos);
                      } else {
                          location = vec3.scale(vec3.create(), direction, plane.normalOffset[3]);
                      }
                      return { location, direction };
                  })
                : undefined,
    };
}

function collectObjectIds(
    highlightedIds: number[],
    hiddenIds: number[],
    selectionBasketIds: number[],
    mainObject: ReturnType<typeof selectMainObject>,
    viewpointDefaultVisibility: boolean,
    pointLines: ReturnType<typeof selectPointLines>,
    measure: ReturnType<typeof selectMeasure>,
): { allObjectIds: Set<number>; selectedIds: number[]; exceptionIds: number[] } {
    let selectedIds = highlightedIds;
    const allObjectIds = new Set(selectedIds);

    if (mainObject) {
        // Viewpoint doesn't have notion of main object, so we're going to always put it as the first object in the selection,
        // and restore main object from the first selected object when applying the viewpoint.
        selectedIds = [mainObject, ...selectedIds.filter((id) => id !== mainObject)];
        allObjectIds.add(mainObject);
    }

    const exceptionIds = viewpointDefaultVisibility ? hiddenIds : selectionBasketIds;
    for (const id of exceptionIds) {
        allObjectIds.add(id);
    }

    for (const pointLine of pointLines) {
        for (const point of pointLine.points) {
            if (point.objectId) {
                allObjectIds.add(point.objectId);
            }
        }
    }

    for (const entity of measure.selectedEntities) {
        for (const point of entity) {
            if (point.ObjectId) {
                allObjectIds.add(point.ObjectId);
            }
        }
    }

    return { allObjectIds, selectedIds, exceptionIds };
}

function buildMeasureEntities(
    measure: ReturnType<typeof selectMeasure>,
    objectIdsToPublicIds: Map<number, string>,
): MeasureMeasurements | undefined {
    if (measure.activeAxis.length === 0 || measure.selectedEntities.some((entitySet) => entitySet.length === 0)) {
        return undefined;
    }

    return {
        active_axis: measure.activeAxis.map((axis) => ({
            x: axis?.x ?? true,
            y: axis?.y ?? true,
            z: axis?.z ?? true,
            planar: axis?.planar ?? true,
            result: axis?.result ?? true,
            normal: axis?.normal ?? true,
        })),
        entities: measure.selectedEntities.map((entitySet) =>
            entitySet.map((entity) =>
                entity.drawKind === "vertex"
                    ? {
                          object_id: entity.ObjectId ? objectIdsToPublicIds.get(entity.ObjectId) : undefined,
                          draw_kind: "vertex",
                          path_index: entity.pathIndex,
                          instance_index: entity.instanceIndex,
                          parameter: entity.parameter,
                      }
                    : {
                          object_id: entity.ObjectId ? objectIdsToPublicIds.get(entity.ObjectId) : undefined,
                          draw_kind: entity.drawKind,
                          kind: entity.kind,
                          path_index: entity.pathIndex,
                          instance_index: entity.instanceIndex,
                          parameter: entity.parameter,
                          tangent: entity.tangent,
                          normal: entity.normal,
                          curve_part: entity.curvePart,
                      },
            ),
        ),
    };
}

function buildMeasurements(
    areas: ReturnType<typeof selectAreas>,
    pointLines: ReturnType<typeof selectPointLines>,
    coordinates: ReturnType<typeof selectCoordinates>,
    outlineLasers: ReturnType<typeof selectOutlineLasers>,
    viewpointMeasure: MeasureMeasurements | undefined,
    objectIdsToPublicIds: Map<number, string>,
): Viewpoint["measurements"] {
    const hasAreas = areas.some((a) => a.points.length);
    const hasPointLines = pointLines.some((line) => line.points.length);
    const hasCoordinates = coordinates.length > 0;
    const hasOutlineLasers = outlineLasers.length > 0;
    const hasViewpointMeasure = Boolean(viewpointMeasure);

    const hasMeasurements = hasAreas || hasPointLines || hasCoordinates || hasOutlineLasers || hasViewpointMeasure;

    if (!hasMeasurements) {
        return;
    }

    return {
        area: hasAreas
            ? {
                  areas: areas.map((a) => ({ points: a.points })),
              }
            : undefined,
        point_line: hasPointLines
            ? {
                  point_lines: pointLines.map((line) =>
                      line.points.map(({ snap: { connection: _c, ...snap }, position, objectId }) => ({
                          position,
                          object_id: objectId ? objectIdsToPublicIds.get(objectId) : undefined,
                          snap:
                              snap.type === "corner" || snap.type === "tangent" || snap.type === "midpoint"
                                  ? snap
                                  : undefined,
                      })),
                  ),
              }
            : undefined,
        coordinates: hasCoordinates
            ? coordinates.map((c) => ({
                  id: c.id,
                  point: {
                      position: c.point.position,
                      guides: c.point.guides,
                      snap_type: c.point.snapType,
                  },
                  normal: c.normal,
              }))
            : undefined,
        measure: viewpointMeasure,
        manhole: undefined, // TODO: Implement manhole measurements
        lasers: hasOutlineLasers
            ? (outlineLasers.map((t) => {
                  const measurement_x = copyTraceMeasurement(t.measurementX, t.left, t.right);
                  const measurement_y = copyTraceMeasurement(t.measurementY, t.down, t.up);
                  const measurement_z = copyTraceMeasurement(t.measurementZ, t.zDown, t.zUp);

                  return {
                      laser_position: t.laserPosition,
                      x_dir: t.xDir ?? vec3.fromValues(1, 0, 0),
                      z_dir: t.zDir ?? vec3.fromValues(0, 0, 1),
                      measurement_x,
                      measurement_y,
                      measurement_z,
                      attached_to_plane: t.attachedToPlane ?? true,
                  };
              }) as MeasureLaser[])
            : undefined,
    };
}

function buildGroups(groups: ReturnType<typeof useLazyObjectGroups>["current"]): Viewpoint["groups"] {
    if (groups.length === 0) {
        return;
    }
    const result = groups
        .filter((group) => !isInternalGroup(group))
        .filter((group) => group.status !== ObjectGroupStatus.None)
        .map(({ id, status }) => {
            let mappedStatus: "selected" | "frozen" | "hidden" | "none";
            switch (status) {
                case ObjectGroupStatus.Frozen:
                    mappedStatus = "frozen";
                    break;
                case ObjectGroupStatus.Hidden:
                    mappedStatus = "hidden";
                    break;
                case ObjectGroupStatus.Selected:
                    mappedStatus = "selected";
                    break;
                default:
                    mappedStatus = "none";
            }
            return { id, status: mappedStatus };
        });

    return result.length > 0 ? result : undefined;
}

async function createAndUploadSnapshot(options: {
    id: string;
    width: number;
    height: number;
    type: "jpeg" | "png";
    companyId: number;
    projectId: number;
}): Promise<Snapshot> {
    const snapshotArrayBuffer = await createSnapshotImgAsArrayBuffer({
        width: options.width,
        height: options.height,
        type: options.type,
    });
    const fileName = `viewpoint_snapshot_${options.id}.${options.type}`;
    const contentType = `image/${options.type}`;
    const resp = await uploadToFas({
        companyId: options.companyId,
        projectId: options.projectId,
        fileName,
        contentType,
        file: snapshotArrayBuffer,
    });
    return {
        id: resp.id,
        name: fileName,
        content_type: contentType,
    };
}

/** Pure derivation of analytics properties when a viewpoint is created/captured. */
export function getViewpointCreatedProperties(viewpoint: GetViewpointResult) {
    return {
        has_snapshot: Boolean(viewpoint.snapshot),
        has_selection: Boolean(viewpoint.selection?.length),
        has_clipping: Boolean(viewpoint.clipping?.enabled),
        has_groups: Boolean(viewpoint.groups?.length),
        has_measurements: Boolean(viewpoint.measurements),
    };
}
